/**
 * Project-relative paths.
 *
 * DATA DEFINITION
 *   A Path is a String of the shape "a/b/c.SLDPRT":
 *     - segments separated by a single forward slash "/"
 *     - no leading slash, no trailing slash
 *     - no "." or ".." segments
 *     - no empty segments
 *   Interpretation: the location of a file or folder inside ONE project. Paths
 *   are project-relative, so the same Path in two projects denotes two
 *   different files. Segment case is preserved (engineers name files
 *   "30kV_Cathode Cup_Roof.SLDPRT" and that casing is meaningful to them), but
 *   comparison for *collision* purposes is case-insensitive, because the
 *   engineers' Windows workstations cannot hold two files whose names differ
 *   only by case.
 *
 * WHY THIS MODULE EXISTS
 *   The seed data comes from git on Windows: paths arrive with backslashes,
 *   stray leading slashes, and spaces. Every path entering the system goes
 *   through `parsePath` exactly once, at the boundary. Everything downstream
 *   may then assume the invariants above.
 */
import { ok, err } from './result.js';
/** Segments that would let a path escape its project root. */
const TRAVERSAL = new Set(['.', '..']);
/**
 * Parse an untrusted path string into a Path.
 * Accepts Windows separators and redundant slashes; rejects traversal.
 * parsePath : String -> Result<Path>
 */
export function parsePath(raw) {
    if (typeof raw !== 'string')
        return err('path must be a string');
    const segments = raw.replace(/\\/g, '/').split('/').filter(s => s.length > 0);
    if (segments.length === 0)
        return err('path is empty');
    for (const s of segments) {
        if (TRAVERSAL.has(s))
            return err(`path segment "${s}" is not allowed`);
        if (s.includes('\0'))
            return err('path contains a null byte');
    }
    return ok(segments.join('/'));
}
/**
 * Split a Path into its segments.
 * segmentsOf : Path -> [String]   (always at least one element)
 */
export const segmentsOf = (p) => p.split('/');
/**
 * The final segment of a Path — the file or folder's own name.
 * basename : Path -> String
 */
export function basename(p) {
    const segs = segmentsOf(p);
    return segs[segs.length - 1];
}
/**
 * The containing folder of a Path, or undefined when the Path is at the
 * project root (a root-level file has no parent folder).
 * dirname : Path -> Path | undefined
 */
export function dirname(p) {
    const segs = segmentsOf(p);
    return segs.length <= 1 ? undefined : segs.slice(0, -1).join('/');
}
/**
 * Every ancestor folder of a Path, outermost first.
 * "a/b/c.pdf" -> ["a", "a/b"].  A root-level file yields [].
 * ancestorsOf : Path -> [Path]
 */
export function ancestorsOf(p) {
    const segs = segmentsOf(p);
    const out = [];
    for (let i = 1; i < segs.length; i++)
        out.push(segs.slice(0, i).join('/'));
    return out;
}
/**
 * The lowercased extension of a Path WITHOUT the dot, or "" when there is none.
 * Engineers mix ".PDF" and ".pdf"; we normalise for classification only.
 * extensionOf : Path -> String
 */
export function extensionOf(p) {
    const name = basename(p);
    const dot = name.lastIndexOf('.');
    return dot <= 0 ? '' : name.slice(dot + 1).toLowerCase();
}
/**
 * The name of a file without its extension.
 * stemOf : Path -> String
 */
export function stemOf(p) {
    const name = basename(p);
    const dot = name.lastIndexOf('.');
    return dot <= 0 ? name : name.slice(0, dot);
}
/** Append a child segment to a folder Path. joinPath : Path String -> Result<Path> */
export const joinPath = (parent, child) => parsePath(`${parent}/${child}`);
/**
 * The Path that results from giving `p` a new final segment.
 * Used by rename: only the leaf changes, the folder is untouched.
 * renameLeaf : Path String -> Result<Path>
 */
export function renameLeaf(p, newName) {
    if (newName.includes('/') || newName.includes('\\'))
        return err('a file name may not contain a path separator');
    if (newName.trim().length === 0)
        return err('a file name may not be blank');
    const parent = dirname(p);
    return parent ? joinPath(parent, newName) : parsePath(newName);
}
/**
 * The Path that results from moving `p` out of `fromPrefix` and into
 * `toPrefix`. Returns undefined when `p` is not inside `fromPrefix`.
 * Used when a whole folder is renamed: every descendant moves with it.
 * reparent : Path Path Path -> Path | undefined
 */
export function reparent(p, fromPrefix, toPrefix) {
    if (p === fromPrefix)
        return toPrefix;
    if (!p.startsWith(`${fromPrefix}/`))
        return undefined;
    return `${toPrefix}${p.slice(fromPrefix.length)}`;
}
/**
 * A case-folded key for collision detection.
 * Two Paths collide when their keys are equal.
 * pathKey : Path -> String
 */
export const pathKey = (p) => p.toLowerCase();
/** True when `p` is `folder` itself or lies inside it. isInside : Path Path -> Boolean */
export const isInside = (p, folder) => p === folder || p.startsWith(`${folder}/`);
/**
 * Order paths the way a file browser does: by segment, folders' contents
 * grouped together, case-insensitive, numbers compared numerically so that
 * "Part10" sorts after "Part9".
 * comparePaths : Path Path -> Number
 */
export function comparePaths(a, b) {
    const as = segmentsOf(a);
    const bs = segmentsOf(b);
    const n = Math.min(as.length, bs.length);
    for (let i = 0; i < n; i++) {
        const c = compareNames(as[i], bs[i]);
        if (c !== 0)
            return c;
    }
    return as.length - bs.length;
}
/** Compare two single names naturally (case-insensitive, digit-aware). */
export function compareNames(a, b) {
    return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
}
//# sourceMappingURL=path.js.map